import React, { lazy, Suspense, memo, useCallback, startTransition, useState } from 'react';
import { DELETE_MODAL, RENAME_FILE_MODAL, RENAME_MODAL, UPLOAD_MODAL } from '@constants/common';
import { useSelector } from 'react-redux';
import Spinner from '../Spinner';

// Lazy load modals for better performance
const DeleteModal = lazy(() => import('@components/Modal/DeleteModal'));
const RenameModal = lazy(() => import('@components/Modal/RenameModal'));
const OpenapiUpload = lazy(() => import('@components/OpenapiUpload'));
const MoveFileModal = lazy(() => import('@components/Modal/MoveFileModal'));
const CreateDocuments = lazy(() => import('@components/CreateDocuments/CreateDocuments'));

/**
 * Component for rendering file operation modals
 * @param {Object} props - Component props
 * @param {Object} props.modalState - Modal state from useModalState hook
 * @param {Function} props.closeModal - Function to close modal
 * @param {Object} props.fileActions - File actions from useFileActions hook
 * @returns {JSX.Element} File operation modals
 */
const FileOperationModals = ({ modalState, closeModal, fileActions }) => {
  const [isPending, startModalTransition] = useState(false);

  const {
    handleDeleteFile,
    handleDeleteFolder,
    handleRenameFile,
    handleRenameFolder,
    handleMoveFile,
    handleCopyFile,
    handleMoveFolder,
  } = fileActions;

  const { visible, type, isFolder, detail, id } = modalState;
  const folderMappings = useSelector((state) => state.organization?.folderMappings);
  const updateBitbucketFiles = useSelector((state) => state.organization?.loading?.updateBitbucketFiles);
  const activeWorkspace = useSelector((state) => state.organization?.activeWorkspace);
  const workspaceList = useSelector((state) => state.organization?.workspaceList);

  // Handle delete operation
  const onDeleteConfirm = useCallback(() => {
    if (isFolder) {
      handleDeleteFolder(id);
    } else {
      handleDeleteFile(id);
    }
  }, [isFolder, id, handleDeleteFile, handleDeleteFolder]);

  // Handle rename operation
  const onRenameSubmit = useCallback(
    (newName) => {
      if (isFolder) {
        handleRenameFolder(id, newName);
      } else {
        handleRenameFile(id, newName);
      }
    },
    [isFolder, handleRenameFile, handleRenameFolder, id]
  );

  // Get file name from folder mappings
  const getFileName = useCallback(
    (id) => {
      const { title } = folderMappings?.[id] || { title: '' };
      return title.split('.')[0];
    },
    [folderMappings]
  );

  const safeCloseModal = useCallback(() => {
    startTransition(() => {
      closeModal();
    });
  }, [closeModal]);

  return (
    <Suspense
      fallback={
        <div>
          <Spinner />
        </div>
      }
    >
      <>
        <CreateDocuments />

        {visible && (
          <>
            {type === DELETE_MODAL && (
              <DeleteModal
                open={visible}
                onCancel={safeCloseModal}
                onSubmit={onDeleteConfirm}
                loading={updateBitbucketFiles}
              />
            )}

            {(type === RENAME_MODAL || type === RENAME_FILE_MODAL) && (
              <RenameModal
                open={visible}
                onCancel={safeCloseModal}
                onSubmit={onRenameSubmit}
                title={isFolder ? 'Rename Collection' : 'Rename File'}
                defaultInputValue={getFileName(id)}
              />
            )}

            {type === UPLOAD_MODAL && (
              <OpenapiUpload
                folder_id={id}
                workspace_id={activeWorkspace?.id}
                open={visible}
                onCancel={safeCloseModal}
              />
            )}

            {type === 'MOVE_FILE' && (
              <MoveFileModal
                open={visible}
                onCancel={safeCloseModal}
                handleMoveFolder={handleMoveFolder}
                handleMoveFile={handleMoveFile}
                copyFileHandler={handleCopyFile}
                isFolder={isFolder}
                id={id}
                detail={detail}
                availableWorkspaces={workspaceList}
              />
            )}
          </>
        )}
      </>
    </Suspense>
  );
};

export default memo(FileOperationModals);
